import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TruckIcon, MapPinIcon, ClockIcon, DollarSignIcon, FilterIcon, BookmarkIcon, RefreshCwIcon, AlertTriangleIcon, CheckCircleIcon, PlusIcon, SearchIcon, CalendarIcon, PackageIcon, FileTextIcon, ArrowRightIcon, XIcon, ChevronDownIcon, ChevronUpIcon, StarIcon, EyeIcon } from 'lucide-react';
import ShipmentConfirmation from '../components/shipments/ShipmentConfirmation';
interface SavedLocation {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}
interface Quote {
  id: string;
  carrierId: string;
  carrierName: string;
  equipmentType: string;
  transitDays: number;
  totalPrice: number;
  linehaul: number;
  fuel: number;
  accessorials: number;
  expiresAt: string;
  services: string[];
  rating: number;
}
interface HistoricalQuote {
  id: string;
  requestDate: string;
  origin: string;
  destination: string;
  equipmentType: string;
  weight: string;
  status: 'Active' | 'Expired' | 'Booked';
  quotesReceived: number;
  bestPrice: number;
  expiresAt: string;
}
interface QuoteRequest {
  originZip: string;
  destinationZip: string;
  equipmentType: string;
  weight: string;
  pickupDate?: string;
  deliveryDate?: string;
  pickupTimeStart?: string;
  pickupTimeEnd?: string;
  deliveryTimeStart?: string;
  deliveryTimeEnd?: string;
  commodity?: string;
  specialRequirements: string[];
  referenceNumbers: Array<{
    type: string;
    value: string;
  }>;
  saveOrigin?: boolean;
  saveDestination?: boolean;
  originLocationName?: string;
  destinationLocationName?: string;
}
interface QuoteFilters {
  dateFrom: string;
  dateTo: string;
  status: string[];
  equipmentType: string;
}
const Quotes = () => {
  const navigate = useNavigate();
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [activeStep, setActiveStep] = useState<'form' | 'results' | 'compare' | 'confirmation' | 'booking'>('form');
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedQuotes, setSelectedQuotes] = useState<string[]>([]);
  const [savedLocations, setSavedLocations] = useState<SavedLocation[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [filteredQuotes, setFilteredQuotes] = useState<Quote[]>([]);
  const [historicalQuotes, setHistoricalQuotes] = useState<HistoricalQuote[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<QuoteFilters>({
    dateFrom: '',
    dateTo: '',
    status: [],
    equipmentType: ''
  });
  // Form state
  const [quoteRequest, setQuoteRequest] = useState<QuoteRequest>({
    originZip: '',
    destinationZip: '',
    equipmentType: 'Dry Van',
    weight: '',
    specialRequirements: [],
    referenceNumbers: []
  });
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const totalPages = 1;
  // Filter state
  // Mock data
  useEffect(() => {
    setSavedLocations([{
      id: '1',
      name: 'ACME Distribution Center',
      address: '123 Industrial Blvd',
      city: 'Pittsburgh',
      state: 'PA',
      zip: '15206'
    }, {
      id: '2',
      name: 'Main Warehouse',
      address: '456 Commerce Dr',
      city: 'Cleveland',
      state: 'OH',
      zip: '44101'
    }]);
    // Mock historical quotes
    setHistoricalQuotes([{
      id: 'QR-001',
      requestDate: '2024-01-15',
      origin: 'Pittsburgh, PA (15206)',
      destination: 'Cleveland, OH (44101)',
      equipmentType: 'Dry Van',
      weight: '42,000 lbs',
      status: 'Booked',
      quotesReceived: 5,
      bestPrice: 2654,
      expiresAt: '2024-01-16T10:00:00Z'
    }, {
      id: 'QR-002',
      requestDate: '2024-01-14',
      origin: 'Chicago, IL (60601)',
      destination: 'Dallas, TX (75201)',
      equipmentType: 'Reefer',
      weight: '38,500 lbs',
      status: 'Active',
      quotesReceived: 3,
      bestPrice: 3120,
      expiresAt: '2024-01-17T14:00:00Z'
    }, {
      id: 'QR-003',
      requestDate: '2024-01-13',
      origin: 'Los Angeles, CA (90210)',
      destination: 'Phoenix, AZ (85001)',
      equipmentType: 'Flatbed',
      weight: '45,000 lbs',
      status: 'Expired',
      quotesReceived: 7,
      bestPrice: 1890,
      expiresAt: '2024-01-14T16:00:00Z'
    }, {
      id: 'QR-004',
      requestDate: '2024-01-12',
      origin: 'Atlanta, GA (30301)',
      destination: 'Miami, FL (33101)',
      equipmentType: 'Dry Van',
      weight: '35,200 lbs',
      status: 'Active',
      quotesReceived: 4,
      bestPrice: 1456,
      expiresAt: '2024-01-16T12:00:00Z'
    }, {
      id: 'QR-005',
      requestDate: '2024-01-11',
      origin: 'Seattle, WA (98101)',
      destination: 'Portland, OR (97201)',
      equipmentType: 'Dry Van',
      weight: '28,750 lbs',
      status: 'Booked',
      quotesReceived: 6,
      bestPrice: 890,
      expiresAt: '2024-01-12T09:00:00Z'
    }]);
  }, []);
  const handleInputChange = (field: keyof QuoteRequest, value: any) => {
    setQuoteRequest(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSpecialRequirementToggle = (requirement: string) => {
    setQuoteRequest(prev => ({
      ...prev,
      specialRequirements: prev.specialRequirements.includes(requirement) ? prev.specialRequirements.filter(r => r !== requirement) : [...prev.specialRequirements, requirement]
    }));
  };
  const addReferenceNumber = () => {
    setQuoteRequest(prev => ({
      ...prev,
      referenceNumbers: [...prev.referenceNumbers, {
        type: 'PO',
        value: ''
      }]
    }));
  };
  const updateReferenceNumber = (index: number, field: 'type' | 'value', value: string) => {
    setQuoteRequest(prev => ({
      ...prev,
      referenceNumbers: prev.referenceNumbers.map((ref, i) => i === index ? {
        ...ref,
        [field]: value
      } : ref)
    }));
  };
  const removeReferenceNumber = (index: number) => {
    setQuoteRequest(prev => ({
      ...prev,
      referenceNumbers: prev.referenceNumbers.filter((_, i) => i !== index)
    }));
  };
  const generateQuotes = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const mockQuotes: Quote[] = [{
        id: '1',
        carrierId: 'carrier-1',
        carrierName: 'Fast Freight Express',
        equipmentType: quoteRequest.equipmentType,
        transitDays: 1,
        totalPrice: 2847,
        linehaul: 2200,
        fuel: 440,
        accessorials: 207,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        services: ['Tracking', 'Insurance', 'Liftgate'],
        rating: 4.8
      }, {
        id: '2',
        carrierId: 'carrier-2',
        carrierName: 'Reliable Transport Co',
        equipmentType: quoteRequest.equipmentType,
        transitDays: 2,
        totalPrice: 2654,
        linehaul: 2100,
        fuel: 420,
        accessorials: 134,
        expiresAt: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
        services: ['Tracking', 'Insurance'],
        rating: 4.6
      }, {
        id: '3',
        carrierId: 'carrier-3',
        carrierName: 'Premium Logistics',
        equipmentType: quoteRequest.equipmentType,
        transitDays: 1,
        totalPrice: 3120,
        linehaul: 2400,
        fuel: 480,
        accessorials: 240,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        services: ['Tracking', 'Insurance', 'White Glove', 'Liftgate'],
        rating: 4.9
      }];
      setQuotes(mockQuotes.sort((a, b) => a.totalPrice - b.totalPrice));
      setFilteredQuotes(mockQuotes.sort((a, b) => a.totalPrice - b.totalPrice));
      setActiveStep('results');
      setLoading(false);
    }, 1000);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Booked':
        return 'bg-blue-100 text-blue-800';
      case 'Expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const openQuoteForm = () => {
    setShowQuoteForm(true);
    setActiveStep('form');
    // Reset form state
    setQuoteRequest({
      originZip: '',
      destinationZip: '',
      equipmentType: 'Dry Van',
      weight: '',
      specialRequirements: [],
      referenceNumbers: []
    });
  };
  const closeQuoteForm = () => {
    setShowQuoteForm(false);
    setActiveStep('form');
  };
  // Main quotes dashboard view
  if (!showQuoteForm) {
    return <div className="py-4" data-id="element-951">
        <div className="mb-6" data-id="element-952">
          <div className="flex justify-between items-center" data-id="element-953">
            <div data-id="element-954">
              <h1 className="text-2xl font-semibold text-gray-800" data-id="element-955">
                Quote Management
              </h1>
              <p className="text-gray-600" data-id="element-956">
                View and manage your freight quotes
              </p>
            </div>
            <button onClick={openQuoteForm} className="px-4 py-2 bg-primary-700 text-white rounded-lg text-sm font-medium hover:bg-primary-800 flex items-center" data-id="element-957">
              <PlusIcon size={16} className="mr-2" data-id="element-958" />
              Get New Quote
            </button>
          </div>
        </div>
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4" data-id="element-959">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-960">
            <div data-id="element-961">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-962">
                Date Range
              </label>
              <div className="space-y-2" data-id="element-963">
                <input type="date" value={filters.dateFrom} onChange={e => setFilters(prev => ({
                ...prev,
                dateFrom: e.target.value
              }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-964" />
                <input type="date" value={filters.dateTo} onChange={e => setFilters(prev => ({
                ...prev,
                dateTo: e.target.value
              }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-965" />
              </div>
            </div>
            <div data-id="element-966">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-967">
                Status
              </label>
              <div className="space-y-2" data-id="element-968">
                {['Active', 'Expired', 'Booked'].map(status => <label key={status} className="flex items-center" data-id="element-969">
                    <input type="checkbox" checked={filters.status.includes(status)} onChange={() => {
                  setFilters(prev => ({
                    ...prev,
                    status: prev.status.includes(status) ? prev.status.filter(s => s !== status) : [...prev.status, status]
                  }));
                }} className="h-4 w-4 text-primary-600 rounded border-gray-300" data-id="element-970" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-971">{status}</span>
                  </label>)}
              </div>
            </div>
            <div data-id="element-972">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-973">
                Equipment Type
              </label>
              <select value={filters.equipmentType} onChange={e => setFilters(prev => ({
              ...prev,
              equipmentType: e.target.value
            }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-974">
                <option value="" data-id="element-975">All Types</option>
                <option value="Dry Van" data-id="element-976">Dry Van</option>
                <option value="Reefer" data-id="element-977">Reefer</option>
                <option value="Flatbed" data-id="element-978">Flatbed</option>
              </select>
            </div>
            <div className="flex items-end" data-id="element-979">
              <button onClick={() => {
              setFilters({
                dateFrom: '',
                dateTo: '',
                status: [],
                equipmentType: ''
              });
              setCurrentPage(1);
            }} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-980">
                Clear Filters
              </button>
            </div>
          </div>
        </div>
        {/* Quote History */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200" data-id="element-981">
          <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center" data-id="element-982">
            <h2 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-983">
              <FileTextIcon size={18} className="mr-2 text-primary-700" data-id="element-984" />
              Recent Quote Requests
            </h2>
            <div className="flex items-center space-x-2" data-id="element-985">
              <span className="text-sm text-gray-600" data-id="element-986">Show</span>
              <select value={pageSize} onChange={e => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }} className="px-2 py-1 border border-gray-300 rounded-md text-sm" data-id="element-987">
                <option value="5" data-id="element-988">5</option>
                <option value="10" data-id="element-989">10</option>
                <option value="25" data-id="element-990">25</option>
                <option value="50" data-id="element-991">50</option>
              </select>
              <span className="text-sm text-gray-600" data-id="element-992">entries</span>
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-993">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-994">
              {/* Update the table header structure */}
              <thead className="bg-gray-50" data-id="element-995">
                <tr data-id="element-996">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-997">
                    Quote Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-998">
                    Equipment
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-999">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1000">
                    Best Price
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1001">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1002">
                {/* Calculate filtered quotes based on filters */}
                {historicalQuotes.map(quote => <tr key={quote.id} className="hover:bg-gray-50" data-id="element-1003">
                    <td className="px-4 py-4 whitespace-nowrap" data-id="element-1004">
                      <div className="flex flex-col" data-id="element-1005">
                        <span className="text-sm font-medium text-primary-700 mb-1" data-id="element-1006">
                          {quote.id}
                        </span>
                        <div className="text-sm text-gray-900 flex items-center mb-1" data-id="element-1007">
                          <MapPinIcon size={14} className="mr-1.5 text-gray-400" data-id="element-1008" />
                          {quote.origin}
                        </div>
                        <div className="text-sm text-gray-900 flex items-center" data-id="element-1009">
                          <MapPinIcon size={14} className="mr-1.5 text-gray-400" data-id="element-1010" />
                          {quote.destination}
                        </div>
                        <span className="text-xs text-gray-500 mt-1" data-id="element-1011">
                          {new Date(quote.requestDate).toLocaleDateString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" data-id="element-1012">
                      <div className="flex flex-col" data-id="element-1013">
                        <span className="text-sm text-gray-900" data-id="element-1014">
                          {quote.equipmentType}
                        </span>
                        <span className="text-sm text-gray-500" data-id="element-1015">
                          {quote.weight}
                        </span>
                        <span className="text-xs text-gray-500 mt-1" data-id="element-1016">
                          {quote.quotesReceived} quotes received
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" data-id="element-1017">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`} data-id="element-1018">
                        {quote.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" data-id="element-1019">
                      <span className="text-lg font-semibold text-gray-900" data-id="element-1020">
                        ${quote.bestPrice.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" data-id="element-1021">
                      <div className="flex flex-col items-center gap-2" data-id="element-1022">
                        {quote.status === 'Active' && <button onClick={() => {
                      const selectedQuote = {
                        id: quote.id,
                        carrierId: 'carrier-1',
                        carrierName: quote.carrier,
                        totalPrice: quote.bestPrice,
                        transitDays: 2,
                        services: []
                      };
                      setSelectedQuote(selectedQuote);
                      setActiveStep('confirmation');
                      setShowQuoteForm(true);
                    }} className="w-full px-3 py-2 bg-primary-700 text-white rounded hover:bg-primary-800 flex items-center justify-center" data-id="element-1023">
                            <TruckIcon size={14} className="mr-1.5" data-id="element-1024" />
                            Book Now
                          </button>}
                        {quote.status === 'Expired' && <button onClick={openQuoteForm} className="w-full px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center justify-center" data-id="element-1025">
                            <RefreshCwIcon size={14} className="mr-1.5" data-id="element-1026" />
                            Re-quote
                          </button>}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between" data-id="element-1027">
            <div className="text-sm text-gray-700" data-id="element-1028">
              Showing {(currentPage - 1) * pageSize + 1} to{' '}
              {Math.min(currentPage * pageSize, historicalQuotes.length)} of{' '}
              {historicalQuotes.length} entries
            </div>
            <div className="flex space-x-2" data-id="element-1029">
              <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1030">
                Previous
              </button>
              {Array.from({
              length: totalPages
            }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 border rounded-md text-sm font-medium ${currentPage === page ? 'bg-primary-50 border-primary-500 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1031">
                  {page}
                </button>)}
              <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1032">
                Next
              </button>
            </div>
          </div>
          {historicalQuotes.length === 0 && <div className="text-center py-8" data-id="element-1033">
              <FileTextIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-1034" />
              <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1035">
                No quotes yet
              </h3>
              <p className="text-gray-500 mb-4" data-id="element-1036">
                Get started by requesting your first freight quote
              </p>
              <button onClick={openQuoteForm} className="px-4 py-2 bg-primary-700 text-white rounded-lg text-sm font-medium hover:bg-primary-800" data-id="element-1037">
                Get Your First Quote
              </button>
            </div>}
        </div>
      </div>;
  }
  // Quote form modal/overlay views
  if (activeStep === 'form') {
    return <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-1038">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-1039">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center" data-id="element-1040">
            <h2 className="text-xl font-semibold text-gray-800" data-id="element-1041">
              Get Quick Quote
            </h2>
            <button onClick={closeQuoteForm} className="text-gray-400 hover:text-gray-500" data-id="element-1042">
              <XIcon size={20} data-id="element-1043" />
            </button>
          </div>
          <form onSubmit={e => {
          e.preventDefault();
          generateQuotes();
        }} data-id="element-1044">
            <div className="p-4 space-y-4" data-id="element-1045">
              {/* Basic Quote Information */}
              <div className="border-b border-gray-200 pb-4" data-id="element-1046">
                <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center" data-id="element-1047">
                  <TruckIcon size={18} className="mr-2 text-primary-700" data-id="element-1048" />
                  Quote Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3" data-id="element-1049">
                  <div data-id="element-1050">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1051">
                      Origin ZIP *
                    </label>
                    <input type="text" value={quoteRequest.originZip} onChange={e => handleInputChange('originZip', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" placeholder="15206" required data-id="element-1052" />
                  </div>
                  <div data-id="element-1053">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1054">
                      Destination ZIP *
                    </label>
                    <input type="text" value={quoteRequest.destinationZip} onChange={e => handleInputChange('destinationZip', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" placeholder="44101" required data-id="element-1055" />
                  </div>
                  <div data-id="element-1056">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1057">
                      Equipment Type *
                    </label>
                    <select value={quoteRequest.equipmentType} onChange={e => handleInputChange('equipmentType', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" required data-id="element-1058">
                      <option value="Dry Van" data-id="element-1059">Dry Van</option>
                      <option value="Reefer" data-id="element-1060">Reefer</option>
                      <option value="Flatbed" data-id="element-1061">Flatbed</option>
                      <option value="Step Deck" data-id="element-1062">Step Deck</option>
                    </select>
                  </div>
                  <div data-id="element-1063">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1064">
                      Weight (lbs) *
                    </label>
                    <input type="number" value={quoteRequest.weight} onChange={e => handleInputChange('weight', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" placeholder="42000" required data-id="element-1065" />
                  </div>
                </div>
                {/* Saved Locations */}
                {savedLocations.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-id="element-1066">
                    <div data-id="element-1067">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1068">
                        Use Saved Origin Location
                      </label>
                      <select onChange={e => {
                    const location = savedLocations.find(l => l.id === e.target.value);
                    if (location) {
                      handleInputChange('originZip', location.zip);
                    }
                  }} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-1069">
                        <option value="" data-id="element-1070">Select saved location</option>
                        {savedLocations.map(location => <option key={location.id} value={location.id} data-id="element-1071">
                            {location.name} - {location.zip}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-1072">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1073">
                        Use Saved Destination Location
                      </label>
                      <select onChange={e => {
                    const location = savedLocations.find(l => l.id === e.target.value);
                    if (location) {
                      handleInputChange('destinationZip', location.zip);
                    }
                  }} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-1074">
                        <option value="" data-id="element-1075">Select saved location</option>
                        {savedLocations.map(location => <option key={location.id} value={location.id} data-id="element-1076">
                            {location.name} - {location.zip}
                          </option>)}
                      </select>
                    </div>
                  </div>}
              </div>
              {/* Detailed Information (Expandable) */}
              <div className="border-b border-gray-200 pb-4" data-id="element-1077">
                <button type="button" onClick={() => setShowDetails(!showDetails)} className="flex items-center justify-between w-full text-left" data-id="element-1078">
                  <h3 className="text-md font-medium text-gray-900 flex items-center" data-id="element-1079">
                    <PackageIcon size={18} className="mr-2 text-primary-700" data-id="element-1080" />
                    Additional Details (Optional)
                  </h3>
                  {showDetails ? <ChevronUpIcon size={20} className="text-gray-500" data-id="element-1081" /> : <ChevronDownIcon size={20} className="text-gray-500" data-id="element-1082" />}
                </button>
                {showDetails && <div className="mt-4 space-y-4" data-id="element-1083">
                    {/* Dates and Times */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1084">
                      {/* Pickup Information */}
                      <div data-id="element-1085">
                        <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1086">
                          Pickup Information
                        </h4>
                        <div className="space-y-3" data-id="element-1087">
                          <div data-id="element-1088">
                            <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1089">
                              Pickup Date
                            </label>
                            <input type="date" value={quoteRequest.pickupDate || ''} onChange={e => handleInputChange('pickupDate', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-1090" />
                          </div>
                          <div data-id="element-1091">
                            <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1092">
                              Time Window
                            </label>
                            <div className="grid grid-cols-2 gap-2" data-id="element-1093">
                              <input type="time" value={quoteRequest.pickupTimeStart || ''} onChange={e => handleInputChange('pickupTimeStart', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-1094" />
                              <input type="time" value={quoteRequest.pickupTimeEnd || ''} onChange={e => handleInputChange('pickupTimeEnd', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-1095" />
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Delivery Information */}
                      <div data-id="element-1096">
                        <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1097">
                          Delivery Information
                        </h4>
                        <div className="space-y-3" data-id="element-1098">
                          <div data-id="element-1099">
                            <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1100">
                              Delivery Date
                            </label>
                            <input type="date" value={quoteRequest.deliveryDate || ''} onChange={e => handleInputChange('deliveryDate', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-1101" />
                          </div>
                          <div data-id="element-1102">
                            <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1103">
                              Time Window
                            </label>
                            <div className="grid grid-cols-2 gap-2" data-id="element-1104">
                              <input type="time" value={quoteRequest.deliveryTimeStart || ''} onChange={e => handleInputChange('deliveryTimeStart', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-1105" />
                              <input type="time" value={quoteRequest.deliveryTimeEnd || ''} onChange={e => handleInputChange('deliveryTimeEnd', e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-1106" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Commodity and Special Requirements */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1107">
                      <div data-id="element-1108">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1109">
                          Commodity Description
                        </label>
                        <input type="text" value={quoteRequest.commodity || ''} onChange={e => handleInputChange('commodity', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" placeholder="Packaged Goods" data-id="element-1110" />
                      </div>
                      <div data-id="element-1111">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1112">
                          Special Requirements
                        </label>
                        <div className="grid grid-cols-2 gap-2" data-id="element-1113">
                          {['Liftgate', 'Appointment Required', 'Inside Delivery', 'Residential'].map(req => <label key={req} className="flex items-center text-xs" data-id="element-1114">
                              <input type="checkbox" checked={quoteRequest.specialRequirements.includes(req)} onChange={() => handleSpecialRequirementToggle(req)} className="h-3 w-3 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-1" data-id="element-1115" />
                              {req}
                            </label>)}
                        </div>
                      </div>
                    </div>
                    {/* Reference Numbers */}
                    <div data-id="element-1116">
                      <div className="flex items-center justify-between mb-2" data-id="element-1117">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-1118">
                          Reference Numbers
                        </label>
                        <button type="button" onClick={addReferenceNumber} className="text-xs text-primary-600 hover:text-primary-700 flex items-center" data-id="element-1119">
                          <PlusIcon size={12} className="mr-1" data-id="element-1120" />
                          Add Reference
                        </button>
                      </div>
                      {quoteRequest.referenceNumbers.map((ref, index) => <div key={index} className="flex gap-2 mb-2" data-id="element-1121">
                          <select value={ref.type} onChange={e => updateReferenceNumber(index, 'type', e.target.value)} className="w-24 px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-1122">
                            <option value="PO" data-id="element-1123">PO</option>
                            <option value="BOL" data-id="element-1124">BOL</option>
                            <option value="REF" data-id="element-1125">REF</option>
                          </select>
                          <input type="text" value={ref.value} onChange={e => updateReferenceNumber(index, 'value', e.target.value)} className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" placeholder="Enter reference number" data-id="element-1126" />
                          <button type="button" onClick={() => removeReferenceNumber(index)} className="px-2 py-1.5 text-xs text-red-600 hover:text-red-700" data-id="element-1127">
                            <XIcon size={12} data-id="element-1128" />
                          </button>
                        </div>)}
                    </div>
                  </div>}
              </div>
            </div>
            {/* Form Actions */}
            <div className="px-4 py-3 bg-gray-50 text-right border-t border-gray-200 flex justify-between" data-id="element-1129">
              <button type="button" onClick={closeQuoteForm} className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50" data-id="element-1130">
                Cancel
              </button>
              <button type="submit" disabled={loading || !quoteRequest.originZip || !quoteRequest.destinationZip || !quoteRequest.weight} className="px-4 py-2 border border-transparent rounded text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 inline-flex items-center" data-id="element-1131">
                {loading ? <>
                    <RefreshCwIcon size={16} className="animate-spin mr-2" data-id="element-1132" />
                    Generating Quotes...
                  </> : <>
                    <SearchIcon size={16} className="mr-2" data-id="element-1133" />
                    Generate Quotes
                  </>}
              </button>
            </div>
          </form>
        </div>
      </div>;
  }
  // Keep existing results, compare, and booking views but wrap them in modal
  if (activeStep === 'results') {
    return <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-1134">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-1135">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center" data-id="element-1136">
            <div data-id="element-1137">
              <h2 className="text-xl font-semibold text-gray-800" data-id="element-1138">
                Quote Results
              </h2>
              <p className="text-sm text-gray-600" data-id="element-1139">
                {filteredQuotes.length} quotes found for{' '}
                {quoteRequest.originZip} → {quoteRequest.destinationZip}
              </p>
            </div>
            <button onClick={closeQuoteForm} className="text-gray-400 hover:text-gray-500" data-id="element-1140">
              <XIcon size={20} data-id="element-1141" />
            </button>
          </div>
          <div className="p-4" data-id="element-1142">
            {/* Quote results content here - keeping existing implementation */}
            <div className="space-y-3" data-id="element-1143">
              {filteredQuotes.map(quote => <div key={quote.id} className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1144">
                  <div className="flex items-center justify-between mb-3" data-id="element-1145">
                    <div className="flex items-center gap-3" data-id="element-1146">
                      <div data-id="element-1147">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-1148">
                          {quote.carrierName}
                          <div className="flex items-center" data-id="element-1149">
                            <StarIcon size={14} className="text-yellow-400 fill-current" data-id="element-1150" />
                            <span className="text-xs text-gray-600 ml-1" data-id="element-1151">
                              {quote.rating}
                            </span>
                          </div>
                        </h3>
                        <p className="text-sm text-gray-600" data-id="element-1152">
                          {quote.equipmentType}
                        </p>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-1153">
                      <div className="text-2xl font-bold text-gray-900" data-id="element-1154">
                        ${quote.totalPrice.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200" data-id="element-1155">
                    <div className="flex items-center space-x-4" data-id="element-1156">
                      <div className="text-sm text-gray-600" data-id="element-1157">
                        Transit Time: {quote.transitDays} days
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-1158">
                        {quote.services.map(service => <span key={service} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded" data-id="element-1159">
                            {service}
                          </span>)}
                      </div>
                    </div>
                    <button onClick={() => {
                  setSelectedQuote(quote);
                  setActiveStep('confirmation');
                }} className="px-4 py-2 bg-primary-700 text-white rounded text-sm font-medium hover:bg-primary-800 flex items-center" data-id="element-1160">
                      <TruckIcon size={16} className="mr-2" data-id="element-1161" />
                      Book This Rate
                    </button>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>;
  }
  if (activeStep === 'compare') {
    const compareQuotes = quotes.filter(q => selectedQuotes.includes(q.id));
    return <div className="py-4" data-id="element-1162">
        <div className="mb-4" data-id="element-1163">
          <button onClick={() => setActiveStep('results')} className="flex items-center text-gray-600 hover:text-gray-900 text-sm mb-2" data-id="element-1164">
            <ArrowRightIcon size={14} className="mr-1 rotate-180" data-id="element-1165" /> Back to
            Results
          </button>
          <h1 className="text-2xl font-semibold text-gray-800" data-id="element-1166">
            Compare Quotes
          </h1>
          <p className="text-gray-600" data-id="element-1167">
            Side-by-side comparison of selected quotes
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-1168">
          <div className="overflow-x-auto" data-id="element-1169">
            <table className="min-w-full" data-id="element-1170">
              <thead className="bg-gray-50" data-id="element-1171">
                <tr data-id="element-1172">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1173">
                    Comparison
                  </th>
                  {compareQuotes.map(quote => <th key={quote.id} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1174">
                      {quote.carrierName}
                    </th>)}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1175">
                <tr data-id="element-1176">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1177">
                    Total Price
                  </td>
                  {compareQuotes.map(quote => <td key={quote.id} className="px-4 py-3 text-center" data-id="element-1178">
                      <span className="text-lg font-bold text-gray-900" data-id="element-1179">
                        ${quote.totalPrice.toLocaleString()}
                      </span>
                    </td>)}
                </tr>
                <tr data-id="element-1180">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1181">
                    Transit Time
                  </td>
                  {compareQuotes.map(quote => <td key={quote.id} className="px-4 py-3 text-center text-sm text-gray-700" data-id="element-1182">
                      {quote.transitDays} day{quote.transitDays > 1 ? 's' : ''}
                    </td>)}
                </tr>
                <tr data-id="element-1183">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1184">
                    Equipment
                  </td>
                  {compareQuotes.map(quote => <td key={quote.id} className="px-4 py-3 text-center text-sm text-gray-700" data-id="element-1185">
                      {quote.equipmentType}
                    </td>)}
                </tr>
                <tr data-id="element-1186">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1187">
                    Linehaul
                  </td>
                  {compareQuotes.map(quote => <td key={quote.id} className="px-4 py-3 text-center text-sm text-gray-700" data-id="element-1188">
                      ${quote.linehaul.toLocaleString()}
                    </td>)}
                </tr>
                <tr data-id="element-1189">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1190">
                    Fuel Surcharge
                  </td>
                  {compareQuotes.map(quote => <td key={quote.id} className="px-4 py-3 text-center text-sm text-gray-700" data-id="element-1191">
                      ${quote.fuel.toLocaleString()}
                    </td>)}
                </tr>
                <tr data-id="element-1192">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1193">
                    Accessorials
                  </td>
                  {compareQuotes.map(quote => <td key={quote.id} className="px-4 py-3 text-center text-sm text-gray-700" data-id="element-1194">
                      ${quote.accessorials.toLocaleString()}
                    </td>)}
                </tr>
                <tr data-id="element-1195">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1196">
                    Services
                  </td>
                  {compareQuotes.map(quote => <td key={quote.id} className="px-4 py-3 text-center" data-id="element-1197">
                      <div className="flex flex-wrap justify-center gap-1" data-id="element-1198">
                        {quote.services.map(service => <span key={service} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded" data-id="element-1199">
                            {service}
                          </span>)}
                      </div>
                    </td>)}
                </tr>
                <tr data-id="element-1200">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1201">
                    Rating
                  </td>
                  {compareQuotes.map(quote => <td key={quote.id} className="px-4 py-3 text-center" data-id="element-1202">
                      <div className="flex items-center justify-center" data-id="element-1203">
                        <StarIcon size={14} className="text-yellow-400 fill-current mr-1" data-id="element-1204" />
                        <span className="text-sm text-gray-700" data-id="element-1205">
                          {quote.rating}
                        </span>
                      </div>
                    </td>)}
                </tr>
                <tr data-id="element-1206">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1207">
                    Actions
                  </td>
                  {compareQuotes.map(quote => <td key={quote.id} className="px-4 py-3 text-center" data-id="element-1208">
                      <button onClick={() => setActiveStep('booking')} className="px-3 py-1.5 bg-primary-700 text-white rounded text-sm font-medium hover:bg-primary-800" data-id="element-1209">
                        Book This Rate
                      </button>
                    </td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  if (activeStep === 'booking') {
    return <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-1210">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4" data-id="element-1211">
          <div className="p-6" data-id="element-1212">
            <div className="flex items-center justify-center py-8" data-id="element-1213">
              <div className="text-center" data-id="element-1214">
                <CheckCircleIcon size={48} className="text-green-600 mx-auto mb-4" data-id="element-1215" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1216">
                  Booking Confirmed!
                </h2>
                <p className="text-gray-600 mb-4" data-id="element-1217">
                  Your load number is:{' '}
                  <span className="font-semibold" data-id="element-1218">LD-789456</span>
                </p>
                <p className="text-sm text-gray-500 mb-6" data-id="element-1219">
                  A confirmation email has been sent to your registered email
                  address.
                </p>
                <div className="flex gap-3 justify-center" data-id="element-1220">
                  <Link to="/shipments/LD-789456" className="px-4 py-2 bg-primary-700 text-white rounded text-sm font-medium hover:bg-primary-800" onClick={closeQuoteForm} data-id="element-1221">
                    View Shipment
                  </Link>
                  <button onClick={closeQuoteForm} className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50" data-id="element-1222">
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // Add the confirmation step view
  if (activeStep === 'confirmation' && selectedQuote) {
    return <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-1223">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-1224">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center" data-id="element-1225">
            <h2 className="text-xl font-semibold text-gray-800" data-id="element-1226">
              Confirm Booking
            </h2>
            <button onClick={closeQuoteForm} className="text-gray-400 hover:text-gray-500" data-id="element-1227">
              <XIcon size={20} data-id="element-1228" />
            </button>
          </div>
          <div className="p-4" data-id="element-1229">
            <ShipmentConfirmation quoteData={quoteRequest} selectedQuote={selectedQuote} onConfirm={() => setActiveStep('booking')} onBack={() => setActiveStep('results')} data-id="element-1230" />
          </div>
        </div>
      </div>;
  }
  return null;
};
export default Quotes;